import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * A card groups things that belong together. That is the whole of its job, and
 * it is deliberately hard to make one look important: no shadow by default, a
 * hairline border, and a surface only one step off the page.
 *
 * Not every section needs one. A page whose every block is boxed has no
 * hierarchy left to express — the boxes stop meaning "these belong together"
 * and start meaning "this is a page". Sections that read fine on the page
 * itself should stay on the page.
 */
const cardVariants = cva('flex flex-col rounded-lg text-foreground', {
  variants: {
    variant: {
      /** The default: bordered, flat, on the page surface. */
      default: 'border border-border bg-surface',
      /** For a card that genuinely floats — a dragged item, a popover panel. */
      raised: 'border border-border bg-surface-raised shadow-sm',
      /** Recessed. Groups reference material without claiming to be content. */
      muted: 'border border-border-subtle bg-surface-muted',
      /** No chrome at all — grouping by spacing alone. */
      plain: '',
    },
    padding: {
      none: '',
      sm: 'p-3.5',
      default: 'p-5',
      lg: 'p-6 sm:p-7',
    },
  },
  defaultVariants: {
    variant: 'default',
    padding: 'default',
  },
});

function Card({
  className,
  variant,
  padding,
  interactive = false,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants> & {
    /** Adds hover and press feedback. Only for a card that is actually clickable. */
    interactive?: boolean;
  }) {
  return (
    <div
      data-slot="card"
      className={cn(
        cardVariants({ variant, padding }),
        interactive && [
          'transition-[border-color,box-shadow,background-color] duration-(--duration-fast) ease-(--ease-out)',
          'hover:border-border-strong hover:shadow-xs',
          'focus-ring',
        ],
        className
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex items-start justify-between gap-4', className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return <h3 data-slot="card-title" className={cn('type-heading', className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('type-body text-foreground-muted', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center gap-3 border-border-subtle border-t pt-4', className)}
      {...props}
    />
  );
}

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, cardVariants };
